import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildSubmitPayload,
  incomeCurrencyHint,
  isFormValid,
  isForeignNationalInMexico,
  resolveFields,
  type FormFieldDef,
} from '../../lib/workflowForm';
import geoService from '../../services/geo.service';
import workflowService from '../../services/workflow.service';
import { colors } from '../../styles/theme';
import DynamicField, { type UploadedFilePointer } from './DynamicField';
import type { UploadProgress } from '../../lib/uploadProgress';
import { prefersReducedMotion, successHoldMs } from '../../lib/successFlash';

/** Submit button palette keyed by visual state (success / disabled / default). */
const SUBMIT_BTN_STYLES = {
  success: { bg: colors.successBg, color: colors.successText },
  disabled: { bg: colors.disabledBg, color: colors.disabledText },
  default: { bg: colors.gold, color: colors.navy },
} as const;

/** Full-width controls (textarea/address/signature) span both grid columns. */
function fieldColSpanClass(controlType: string): string {
  return (
    controlType === 'textarea' ||
    controlType === 'address' ||
    controlType === 'signature'
  )
    ? 'sm:col-span-2'
    : '';
}

interface Props {
  /** Form fields for the case's current state — ordered by display_order. */
  fields: FormFieldDef[];
  /** Captured values keyed by field_key. Initial draft. */
  initialData: Record<string, unknown>;
  /** True while a save / submit network call is in flight. */
  busy?: boolean;
  /** Called with the visible+enabled subset on Save Progress. */
  onSave: (payload: Array<{ field_key: string; value: unknown }>) => void;
  /** Called with the same payload on Submit (caller will then apply
   *  the workflow transition). Disabled until the form is valid. */
  onSubmit: (payload: Array<{ field_key: string; value: unknown }>) => void;
  /** Override label of the submit CTA. Defaults to "Submit & continue". */
  submitLabel?: string;
  /** Required when any field has `control_type=file/signature`. The
   *  parent owns the network call (it knows the caseId + service)
   *  and we delegate per-field uploads to it. The optional `options`
   *  bag carries an AbortSignal so the field can offer a Cancel
   *  button, and a richer onProgress event so the field can show
   *  bytes-remaining. */
  onUploadFile?: (
    fieldKey: string,
    file: File,
    options: {
      onProgress: (progress: UploadProgress) => void;
      signal: AbortSignal;
    },
  ) => Promise<UploadedFilePointer>;
  /** Saved expiry per document — keyed by document_id since one
   *  field_key can have a chain of (re-)uploads, only the current
   *  document's expiry matters in the UI. Optional; absence hides
   *  the expiry inputs entirely. */
  documentExpiries?: Record<string, {
    expires_at: string | null;
    expiry_granularity: 'exact' | 'month' | 'year' | null;
  }>;
  /** Closure that PATCHes the expiry on a specific document. Parent
   *  owns caseId via closure. Same opt-in pattern as `onUploadFile`:
   *  pass the closure to enable the UI. */
  onSaveDocumentExpiry?: (
    documentId: string,
    body: {
      expiry_year?: number | null;
      expiry_month?: number | null;
      expiry_day?: number | null;
      issued_year?: number | null;
      issued_month?: number | null;
      issued_day?: number | null;
    },
  ) => Promise<{ expires_at: string | null; expiry_granularity: 'exact' | 'month' | 'year' | null }>;
  /** Per-field review snapshot from the agent's last review pass.
   *  Drives the client UX: approved fields render locked, rejected
   *  fields show the agent's reason inline. The form engine still
   *  computes `resolveFields()` normally; the review layer is
   *  applied at the DynamicField level. Pass an empty object when
   *  the workflow doesn't have a review feature (contracts, etc.). */
  fieldReviews?: Record<string, {
    review_status: 'pending' | 'approved' | 'rejected';
    rejection_reason: string | null;
    reviewed_at: string | null;
  }>;
  /** When provided, renders a Back button to the left of the action
   *  bar. Used by the pending_profile sub-stepper to navigate to
   *  the previous section without firing a transition. */
  onBack?: () => void;
}

/**
 * Generic form for one workflow state.
 *
 * Owns the local draft state and runs `resolveFields()` on every
 * keystroke so visibility/required/error are always up-to-date.
 * Hidden fields (visibility expression evaluates false) skip
 * validation AND get filtered from the submit payload — that's the
 * contract `fn_validate_case_data` enforces server-side, so the wire
 * shape matches the trigger's expectations.
 *
 * Two CTAs:
 *   · Save progress  — accepts a partial / invalid form (lets the
 *                      client step away mid-flow).
 *   · Submit         — disabled until every visible field is valid.
 */
const DynamicForm = ({
  fields,
  initialData,
  busy = false,
  onSave,
  onSubmit,
  submitLabel = 'Submit & continue',
  onUploadFile,
  documentExpiries,
  onSaveDocumentExpiry,
  fieldReviews,
  onBack,
}: Props): JSX.Element => {
  const [data, setData] = useState<Record<string, unknown>>(() => ({ ...initialData }));
  // Tracks whether the user has edited the form since the last save/submit.
  // The "Save progress" button is available in every state, but a user who
  // doesn't click it would otherwise lose unsaved work on closing the tab —
  // so we arm a native beforeunload prompt while there are pending edits.
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      // Modern browsers show the unsaved-changes prompt on preventDefault
      // alone; the legacy `returnValue` assignment is deprecated.
      e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);
  // The portal's placeholder RFC for a foreign national living in Mexico, fetched once and
  // cached. Null until the request settles; the auto-fill effect below is a no-op while null
  // so the form is fully usable even if the endpoint is unreachable. There is no placeholder
  // CURP: a foreign resident gives the CURP printed on the INM residence card.
  const [kycPlaceholders, setKycPlaceholders] = useState<{ rfc: string } | null>(null);

  // Fields the user has interacted with (blurred or changed). Used
  // to suppress validation messages on first paint so the form
  // doesn't open all-red. The set fills as the user moves through
  // the form; an attempted Submit also fills it in one shot so the
  // remaining gaps surface at once.
  const [touched, setTouched] = useState<Set<string>>(() => new Set());

  const states = useMemo(() => resolveFields(fields, data), [fields, data]);
  const valid = useMemo(() => isFormValid(states), [states]);
  const sections = useMemo(() => groupBySection(states), [states]);

  // Success flash: briefly show a green "✓ Submitted!" label on the
  // submit button after a successful async round-trip (busy true→false).
  const [showSuccess, setShowSuccess] = useState(false);
  const submitAttempted = useRef(false);
  const prevBusy = useRef(busy);
  useEffect(() => {
    const justFinished = prevBusy.current && !busy && submitAttempted.current;
    prevBusy.current = busy;
    if (!justFinished) return undefined;
    submitAttempted.current = false;
    const ms = successHoldMs(prefersReducedMotion());
    if (ms === 0) return undefined; // reduced motion → no flash, instant transition
    setShowSuccess(true);
    const t = setTimeout(() => setShowSuccess(false), ms);
    return () => clearTimeout(t);
  }, [busy]);

  const setField = (key: string, value: unknown): void => {
    setData((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const markTouched = (key: string): void => {
    setTouched((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const markAllVisibleTouched = (): void => {
    setTouched(new Set(states.filter((s) => s.visible).map((s) => s.field.field_key)));
  };

  // Lazy-load the placeholder only when the form contains the RFC field. Avoids a wasted
  // request on workflow states that don't capture it (PEP, documents, etc.).
  const formHasRfc = useMemo(() => fields.some((f) => f.field_key === 'rfc'), [fields]);
  useEffect(() => {
    if (!formHasRfc) return;
    let cancelled = false;
    workflowService
      .getKycPlaceholders()
      .then((g) => {
        if (!cancelled) setKycPlaceholders(g);
      })
      .catch(() => {
        // Silent — the user can still type their own RFC and submit. No spinner / no error
        // UI; auto-fill is a convenience, not a requirement.
      });
    return () => {
      cancelled = true;
    };
  }, [formHasRfc]);

  // Auto-fill RFC with the portal placeholder when the client is a foreign national living
  // in Mexico: Mexican by NEITHER nationality (a dual national is read on both). UNLIKE a
  // real RFC this value is intentionally transient — the client dashboard surfaces a
  // persistent banner reminding the client to replace it once SAT issues one. We never
  // overwrite a user-typed real RFC.
  const foreignInMexico = isForeignNationalInMexico(data);
  useEffect(() => {
    if (!kycPlaceholders || !foreignInMexico) return;
    const current = (data.rfc as string | undefined) ?? '';
    const target = kycPlaceholders.rfc;
    if (current === '' && current !== target) {
      setData((prev) => ({ ...prev, rfc: target }));
    }
  }, [foreignInMexico, data.rfc, kycPlaceholders]);

  // ── SEPOMEX postal code autofill ──────────────────────────────
  // Tracks which address fields were locked by a CP lookup so DynamicField
  // can render them as read-only. Clear the lock when the user changes the
  // CP (they may be correcting it) or switches to a non-MX residence.
  const [cpAutofilledKeys, setCpAutofilledKeys] = useState<Set<string>>(() => {
    // If the form is resuming with a valid saved postal code, pre-seed the
    // autofilled-keys set so a new CP lookup will still overwrite city /
    // municipality / state_residence (rather than leaving stale saved values).
    const savedCp = typeof initialData.postal_code === 'string' ? initialData.postal_code : '';
    if (/^\d{5}$/.test(savedCp)) return new Set(['city', 'municipality', 'state_residence']);
    return new Set();
  });
  const [coloniaChoices, setColoniaChoices] = useState<string[]>([]);
  const cpDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const cp = (typeof data.postal_code === 'string' ? data.postal_code : '').trim();
    const residence = typeof data.country_residence === 'string' ? data.country_residence : '';

    // Autofill is supported for MX (SEPOMEX) and US (GeoNames).
    // CA (Canada Post) is proprietary — degrade to free-text.
    const supportedCountry = residence === 'MX' || residence === 'US';
    if (!supportedCountry || !/^\d{5}$/.test(cp)) {
      setCpAutofilledKeys(new Set());
      setColoniaChoices([]);
      return;
    }

    // Debounce: wait 400 ms after the last keystroke before calling the API.
    if (cpDebounceRef.current) clearTimeout(cpDebounceRef.current);
    let cancelled = false;
    cpDebounceRef.current = setTimeout(async () => {
      const result = await geoService.lookupPostalCode(cp, residence);
      if (cancelled || !result) return;

      // Autofill city, municipality, and state_residence. Only overwrite
      // fields the user hasn't touched with a real value (or that are
      // already a prior autofill). This keeps the user in control.
      const AUTOFILLED = new Set(['city', 'municipality', 'state_residence']);
      setData((prev) => {
        const next = { ...prev };
        if (!next.city || cpAutofilledKeys.has('city'))
          next.city = result.city ?? result.state_name;
        if (!next.municipality || cpAutofilledKeys.has('municipality'))
          next.municipality = result.municipality;
        if (!next.state_residence || cpAutofilledKeys.has('state_residence'))
          next.state_residence = result.state_code;
        return next;
      });

      setCpAutofilledKeys(AUTOFILLED);
      // US has no colonia concept — colonias list will be empty, keeping the
      // colonia field as free-text rather than forcing a dropdown.
      setColoniaChoices(result.colonias);
    }, 400);

    return () => {
      cancelled = true;
      if (cpDebounceRef.current) clearTimeout(cpDebounceRef.current);
    };
  // cpAutofilledKeys intentionally omitted — including it would cause the
  // effect to re-run every time we update it, creating an infinite loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.postal_code, data.country_residence]);

  // ── CURP smart generate ───────────────────────────────────────
  const [curpGenerating, setCurpGenerating] = useState(false);
  const [curpAutoBadge, setCurpAutoBadge] = useState(false);

  // The "Generar CURP" button is shown when all prerequisite fields are
  // present. We don't require birth_state — it maps to 'NE' (born abroad)
  // when absent, which is acceptable for the structural algorithm.
  const canGenerateCurp = useMemo(() => {
    const hasCurpField = fields.some((f) => f.field_key === 'curp');
    return (
      hasCurpField &&
      Boolean(data.first_name) &&
      Boolean(data.last_name) &&
      Boolean(data.birth_date) &&
      (data.gender === 'H' || data.gender === 'M')
    );
  }, [fields, data.first_name, data.last_name, data.birth_date, data.gender]);

  const handleGenerateCurp = useCallback(async () => {
    const rawLastName = (typeof data.last_name === 'string' ? data.last_name : '').trim();
    // Split "Apellido Paterno Materno" → primer + segundo heuristic.
    const parts = rawLastName.split(/\s+/);
    const primerApellido = parts[0] || rawLastName;
    const segundoApellido = parts.slice(1).join(' ');

    setCurpGenerating(true);
    setCurpAutoBadge(false);
    try {
      const result = await workflowService.generateCurp({
        primer_apellido: primerApellido,
        nombre: typeof data.first_name === 'string' ? data.first_name : '',
        fecha_nacimiento: typeof data.birth_date === 'string' ? data.birth_date : '',
        sexo: (data.gender as 'H' | 'M'),
        estado_nacimiento: typeof data.birth_state === 'string' ? data.birth_state : 'NE',
        segundo_apellido: segundoApellido,
      });
      if (result?.curp) {
        setData((prev) => ({ ...prev, curp: result.curp }));
        setCurpAutoBadge(true);
        // Dismiss the badge after 4 s — it's a one-time confirmation.
        setTimeout(() => setCurpAutoBadge(false), 4000);
      }
    } finally {
      setCurpGenerating(false);
    }
  }, [data.first_name, data.last_name, data.birth_date, data.gender, data.birth_state]);

  const handleSave = (): void => {
    onSave(buildSubmitPayload(states));
    // Optimistic: the parent persists the payload. If it fails the user
    // sees an error and the next edit re-arms the guard.
    setDirty(false);
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    // Reveal every remaining error in one shot so the user can see
    // exactly what's missing on their first submit attempt — friendlier
    // than the button just sitting gray. If still invalid we bail
    // BEFORE calling onSubmit, but the errors now show.
    markAllVisibleTouched();
    if (!valid) return;
    submitAttempted.current = true;
    setDirty(false);
    onSubmit(buildSubmitPayload(states));
  };

  const resolveBtnState = (): 'success' | 'disabled' | 'default' => {
    if (showSuccess) return 'success';
    if (busy || !valid) return 'disabled';
    return 'default';
  };
  const btnState = resolveBtnState();
  const btnBg = SUBMIT_BTN_STYLES[btnState].bg;
  const btnColor = SUBMIT_BTN_STYLES[btnState].color;
  let btnLabel: string;
  if (showSuccess) btnLabel = '✓ Submitted!';
  else if (busy) btnLabel = 'Working…';
  else btnLabel = submitLabel;

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {sections.map(({ name, items }) => (
        <section key={name ?? '_default'}>
          {name && (
            <h3
              className="text-xs font-bold uppercase tracking-widest mb-4 pb-2"
              style={{ color: colors.inkSoft, borderBottom: `1px solid ${colors.border}` }}
            >
              {name}
            </h3>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {items.map((s) => {
              if (!s.visible) return null;
              const fk = s.field.field_key;
              const isColonia = fk === 'colonia';
              const isCurp = fk === 'curp';
              // Country-aware nudge: remind US/CA residents to confirm the
              // currency of their income (the field is required + empty, so
              // we guide the choice rather than auto-pick it).
              const incomeHint =
                fk === 'income_currency'
                  ? incomeCurrencyHint(data.country_residence, s.value)
                  : null;
              return (
                <div
                  key={fk}
                  className={fieldColSpanClass(s.field.control_type)}
                >
                  <DynamicField
                    field={s.field}
                    value={s.value}
                    parentValue={
                      s.field.options_filter_field
                        ? data[s.field.options_filter_field]
                        : undefined
                    }
                    enabled={s.enabled}
                    required={s.required}
                    error={s.error}
                    wasTouched={touched.has(fk)}
                    onTouch={() => markTouched(fk)}
                    reviewStatus={fieldReviews?.[fk]?.review_status}
                    rejectionReason={fieldReviews?.[fk]?.rejection_reason ?? null}
                    onChange={(v) => setField(fk, v)}
                    onUploadFile={
                      onUploadFile
                        ? (file, options) => onUploadFile(fk, file, options)
                        : undefined
                    }
                    documentExpiry={
                      // s.value is the file pointer (with document_id)
                      // for file fields; look up the saved expiry by it.
                      (() => {
                        const ptr = s.value as { document_id?: string } | null;
                        const docId = ptr?.document_id;
                        return docId ? documentExpiries?.[docId] : undefined;
                      })()
                    }
                    onSaveDocumentExpiry={onSaveDocumentExpiry}
                    readOnly={cpAutofilledKeys.has(fk)}
                    optionsOverride={isColonia && coloniaChoices.length > 0 ? coloniaChoices : undefined}
                  />
                  {isCurp && canGenerateCurp && (
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        type="button"
                        onClick={handleGenerateCurp}
                        disabled={curpGenerating}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                        style={{
                          background: curpGenerating ? colors.surface : colors.gold,
                          color: curpGenerating ? colors.muted : colors.navy,
                          border: `1px solid ${colors.border}`,
                          cursor: curpGenerating ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {curpGenerating ? 'Generating…' : 'Generate CURP'}
                      </button>
                      {curpAutoBadge && (
                        <span
                          className="text-xs font-medium"
                          style={{ color: colors.success }}
                        >
                          ✓ Auto-generated — verify it matches your document
                        </span>
                      )}
                    </div>
                  )}
                  {incomeHint && (
                    <p className="mt-2 text-xs" style={{ color: colors.warning }} role="note">
                      {incomeHint}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <div
        className="flex items-center justify-between gap-3 pt-4"
        style={{ borderTop: `1px solid ${colors.border}` }}
      >
        <div>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              disabled={busy}
              className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
              style={{
                background: colors.surface,
                color: colors.inkSoft,
                border: `1px solid ${colors.border}`,
                cursor: busy ? 'not-allowed' : 'pointer',
              }}
            >
              ← Back
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            style={{
              background: colors.surface,
              color: colors.inkSoft,
              border: `1px solid ${colors.border}`,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            Save progress
          </button>
          <button
            type="submit"
            disabled={busy || showSuccess}
            className="px-5 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{
              background: btnBg,
              color: btnColor,
              border: showSuccess ? `1px solid ${colors.successBorder}` : 'none',
              // Stay clickable when invalid (but visually muted): the
              // click reveals all errors at once via markAllVisibleTouched,
              // which is friendlier than a button you can't interact with.
              cursor: busy || showSuccess ? 'not-allowed' : 'pointer',
            }}
          >
            {btnLabel}
          </button>
        </div>
      </div>
    </form>
  );
};

function groupBySection<T extends { field: { section: string | null } }>(
  items: T[],
): Array<{ name: string | null; items: T[] }> {
  const order: Array<string | null> = [];
  const buckets = new Map<string | null, T[]>();
  for (const item of items) {
    const key = item.field.section;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = [];
      buckets.set(key, bucket);
      order.push(key);
    }
    bucket.push(item);
  }
  return order
    // Hide system-only sections (kept by the workflow seed for hidden
    // bookkeeping fields like template_version_id).
    .filter((k) => k !== '_system')
    .map((k) => ({ name: k, items: buckets.get(k) ?? [] }));
}

export default DynamicForm;
